import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { manualSessionApi } from "../api/manual-session";

export default function ManualLogin() {
  const qc = useQueryClient();
  const [textInput, setTextInput] = useState("");
  const imgRef = useRef<HTMLImageElement>(null);

  const session = useQuery({
    queryKey: ["manual-session"],
    queryFn: manualSessionApi.check,
    refetchInterval: 10_000,
  });

  const screenshot = useQuery({
    queryKey: ["manual-screenshot"],
    queryFn: manualSessionApi.screenshot,
    enabled: session.data?.active === true,
    refetchInterval: false,
  });

  const startMut = useMutation({
    mutationFn: manualSessionApi.start,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["manual-session"] });
      setTimeout(() => qc.invalidateQueries({ queryKey: ["manual-screenshot"] }), 2000);
    },
  });

  const actionMut = useMutation({
    mutationFn: manualSessionApi.action,
    onSuccess: () => {
      setTimeout(() => qc.invalidateQueries({ queryKey: ["manual-screenshot"] }), 800);
    },
  });

  const saveMut = useMutation({
    mutationFn: manualSessionApi.save,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["manual-session"] });
      qc.invalidateQueries({ queryKey: ["manual-screenshot"] });
    },
  });

  const closeMut = useMutation({
    mutationFn: manualSessionApi.close,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["manual-session"] });
      qc.invalidateQueries({ queryKey: ["manual-screenshot"] });
    },
  });

  const handleScreenshotClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 1280);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 720);
    actionMut.mutate({ type: "click", x, y });
  };

  const handleType = () => {
    if (!textInput.trim()) return;
    actionMut.mutate({ type: "type", text: textInput });
    setTextInput("");
  };

  const handleKey = (key: string) => {
    actionMut.mutate({ type: "press", key });
  };

  const isActive = session.data?.active === true;

  if (!isActive) {
    return (
      <div className="rounded-lg border-2 border-dashed border-slate-300 bg-white p-8 text-center">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">
          Manual Login — claude.ai Session
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Opens a headless browser on the server. Log into claude.ai via the screenshot
          interface below, then click "Save Session" to persist cookies for weekly automation.
        </p>
        <button
          onClick={() => startMut.mutate()}
          disabled={startMut.isPending}
          className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {startMut.isPending ? "Starting browser…" : "Start Manual Login"}
        </button>
        {startMut.isError && (
          <p className="mt-2 text-xs text-rose-600">
            {(startMut.error as { message?: string })?.message ?? "Failed to start"}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Manual Login Session</h3>
          <span className="inline-block mt-0.5 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">
            Active
          </span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
            className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saveMut.isPending ? "Saving…" : "Save Session"}
          </button>
          <button
            onClick={() => closeMut.mutate()}
            disabled={closeMut.isPending}
            className="rounded border border-rose-300 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>

      {saveMut.isSuccess && (
        <div className="bg-emerald-50 px-4 py-2 text-xs text-emerald-700">
          Session saved ({saveMut.data.cookie_count} cookies). Ready for weekly automation.
        </div>
      )}

      {/* Screenshot viewer */}
      <div className="relative bg-slate-900 p-2">
        {screenshot.data?.url && (
          <div className="mb-1 truncate px-1 text-xs text-slate-400">
            {screenshot.data.url}
          </div>
        )}
        {screenshot.data?.screenshot ? (
          <img
            ref={imgRef}
            src={`data:image/png;base64,${screenshot.data.screenshot}`}
            alt="Browser"
            className="w-full cursor-crosshair rounded border border-slate-700"
            onClick={handleScreenshotClick}
          />
        ) : (
          <div className="flex h-48 items-center justify-center text-sm text-slate-500">
            {screenshot.isLoading ? "Loading screenshot…" : "No screenshot available"}
          </div>
        )}
        {actionMut.isPending && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded">
            <span className="text-sm text-white">Processing…</span>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2 border-t border-slate-200 px-4 py-3">
        <input
          type="text"
          value={textInput}
          onChange={(e) => setTextInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleType();
          }}
          placeholder="Type text here…"
          className="flex-1 rounded border border-slate-300 px-3 py-1.5 text-sm"
        />
        <button
          onClick={handleType}
          disabled={!textInput.trim() || actionMut.isPending}
          className="rounded bg-slate-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          Type
        </button>
        <div className="flex gap-1">
          {["Enter", "Tab", "Escape", "Backspace"].map((key) => (
            <button
              key={key}
              onClick={() => handleKey(key)}
              disabled={actionMut.isPending}
              className="rounded border border-slate-300 px-2 py-1.5 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {key}
            </button>
          ))}
        </div>
        <button
          onClick={() => qc.invalidateQueries({ queryKey: ["manual-screenshot"] })}
          className="rounded border border-slate-300 px-2 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
          title="Refresh screenshot"
        >
          Refresh
        </button>
      </div>
    </div>
  );
}
